import React, { memo, useState, useCallback } from 'react';
import ReactNativeBridge from '../../utils/reactNativeBridge';
import newsIcon from '../../assets/img/news-icon.png';

/**
 * Huvle 뉴스 버튼 컴포넌트
 * - 안드로이드 전용: 앱 버전 100.4.9 이상일 때만 표시 (iOS에는 노출 안 함)
 * - 클릭 시 RN으로 OPEN_HUVLE_BROWSER 메시지 전송
 */

/**
 * 앱 WebView에서 플랫폼·버전 파싱
 * URL 쿼리: ?platform=ios|android, ?app_version=1.2.3
 * UA 폴백 지원
 */
const MIN_APP_VERSION_ANDROID = [100, 4, 9]; // 안드로이드 100.4.9 이상

const getPlatformInfo = () => {
  const params = new URLSearchParams(window.location.search);
  let platformFromQuery = params.get('platform')?.toLowerCase();
  let appVersion = params.get('app_version');

  // URL 쿼리 파라미터가 없으면 ReactNativeBridge 캐시(sessionStorage)에서 폴백
  if (!platformFromQuery || !appVersion) {
    const cachedInfo = ReactNativeBridge.getAppInfoSync();
    if (cachedInfo) {
      if (!platformFromQuery && cachedInfo.platform) {
        platformFromQuery = cachedInfo.platform.toLowerCase();
      }
      if (!appVersion && cachedInfo.version) {
        appVersion = cachedInfo.version;
      }
    }
  }

  let platform = null;
  let isAndroid = false;
  let isIOS = false;

  if (platformFromQuery === 'android' || platformFromQuery === 'ios') {
    platform = platformFromQuery;
    isAndroid = platformFromQuery === 'android';
    isIOS = platformFromQuery === 'ios';
  } else {
    const ua = navigator.userAgent;
    isAndroid = /\bAndroid\b/i.test(ua);
    isIOS = /\b(iPhone|iPad|iPod|Macintosh)\b/i.test(ua);
    platform = isAndroid ? 'android' : isIOS ? 'ios' : null;
  }

  return {
    platform,
    isAndroid,
    isIOS,
    appVersion: appVersion || null,
  };
};

// 버전 비교: current >= min (시맨틱)
const isVersionAtLeast = (versionStr, minParts) => {
  const parts = (versionStr || '0.0.0').split('.').map(Number);
  const [ma, mb, mc] = minParts;
  const [a = 0, b = 0, c = 0] = parts;
  if (a > ma) return true;
  if (a < ma) return false;
  if (b > mb) return true;
  if (b < mb) return false;
  return c >= mc;
};

const HuvleNewsButton = memo(() => {
  const [isClicking, setIsClicking] = useState(false);

  const platformInfo = getPlatformInfo();
  const isAndroid = platformInfo.isAndroid;
  const appVersion = platformInfo.appVersion;
  // 안드로이드만 100.4.9 이상 체크 (iOS에는 노출 안 함)
  const versionOk = appVersion ? isVersionAtLeast(appVersion, MIN_APP_VERSION_ANDROID) : false;

  // 안드로이드 + 앱 버전 100.4.9 이상일 때만 표시
  const shouldShow = isAndroid && versionOk;

  const openHuvleBrowser = useCallback(() => {
    if (!ReactNativeBridge.isReactNativeApp()) {
      console.log('⚠️ Huvle: ReactNativeWebView 없음');
      return;
    }

    setIsClicking(true);

    ReactNativeBridge.sendMessage({
      type: 'OPEN_HUVLE_BROWSER',
      requestId: Date.now()
    });

    console.log('✅ Huvle: OPEN_HUVLE_BROWSER 메시지 전송됨');

    setTimeout(() => setIsClicking(false), 300);
  }, []);

  if (!shouldShow) return null;

  return (
    <div style={styles.container}>
      <button
        onClick={openHuvleBrowser}
        style={{
          ...styles.button,
          transform: isClicking ? 'scale(0.9)' : 'scale(1)',
        }}
      >
        <img
          src={newsIcon}
          alt="오늘의 뉴스"
          style={{
            width: '42px',
            height: '42px',
            objectFit: 'contain',
          }}
        />
      </button>

      {/* 라벨 */}
      <div style={styles.label}>
        오늘의 뉴스
      </div>

      {/* CSS 애니메이션 */}
      <style>
        {`
          @keyframes newsPulse {
            0%, 100% { 
              transform: scale(1);
              box-shadow: 0 4px 12px rgba(44, 177, 201, 0.3);
            }
            50% { 
              transform: scale(1.05);
              box-shadow: 0 6px 16px rgba(44, 177, 201, 0.5);
            }
          }

          .huvle-news-button:hover {
            transform: scale(1.1) !important;
            box-shadow: 0 6px 16px rgba(44, 177, 201, 0.5) !important;
          }
        `}
      </style>
    </div>
  );
});

const styles = {
  container: {
    position: 'fixed',
    top: '120px', // WellnessMoneyIcon과 같은 높이
    right: '105px', // 행운의 복권 긁기 버튼과 간격 (30 + 55 + 15)
    zIndex: 1000,
    animation: 'breathe 3s ease-in-out infinite'
  },
  button: {
    width: '55px',
    height: '55px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    border: '2px solid #2CB1C9',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 3px 10px rgba(44, 177, 201, 0.3)',
    transition: 'all 0.3s ease',
    animation: 'newsPulse 2s infinite',
    position: 'relative',
    overflow: 'visible',
    backdropFilter: 'blur(10px)'
  },
  label: {
    position: 'absolute',
    top: '50px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '10px',
    padding: '3px 7px',
    fontSize: '9px',
    fontWeight: '600',
    fontFamily: 'Pretendard',
    color: '#555',
    whiteSpace: 'nowrap',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(5px)'
  }
};

HuvleNewsButton.displayName = 'HuvleNewsButton';

export default HuvleNewsButton;
